import React, { useEffect, useState } from "react";
import "./EmailPreview.scss";

import {
  ReactComponent as ChevronLeft,
  ReactComponent as ChevronRight,
} from "src/images/chevron-left.svg";
import CreatorPlaceholder from "src/images/creator-placeholder.svg";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { API_URLS } from "src/utils/API_URLS";
import axios from "axios";
import {
  decodeHyperlinks,
  decodeMention,
  showErrorToast,
} from "src/utils/methods";
import AnimatedDiv from "src/app/components/AnimatedDiv/AnimatedDiv";
import { useSelector } from "react-redux";
import { RootState } from "src/redux/reducers";
import { useParams } from "react-router";
import XssFilter from "src/app/components/XssFilter/XssFilter";
import { EmailContent } from "./types";

interface Props {
  emailContent: EmailContent;
}

interface PreviewData {
  subject: string;
  message: string;
}

export function EmailPreview(props: Props) {
  const { emailContent } = props;

  const [selectedRecipientIndex, setSelectedRecipientIndex] = useState(0);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);

  const activeWorkspaceUuid = useSelector(
    (state: RootState) => state.mainReducer.activeWorkspaceUuid,
  );

  const params = useParams<{
    projectId: string;
  }>();

  const selectedRecipient = emailContent.recipients[selectedRecipientIndex];

  const toPreviousRecipient = () => {
    if (selectedRecipientIndex === 0) {
      setSelectedRecipientIndex(emailContent.recipients.length - 1);
    } else {
      setSelectedRecipientIndex((v) => v - 1);
    }
  };

  const toNextRecipient = () => {
    if (selectedRecipientIndex === emailContent.recipients.length - 1) {
      setSelectedRecipientIndex(0);
    } else {
      setSelectedRecipientIndex((v) => v + 1);
    }
  };

  useEffect(() => {
    if (selectedRecipient?.wsTaskUuid) {
      getPreviewData();
    }
  }, [selectedRecipient?.wsTaskUuid]);

  const getPreviewData = async () => {
    setPreviewData(null);
    try {
      const { data: response } = await axios.post(API_URLS.getMessagePreview, {
        wsTaskUuid: selectedRecipient?.wsTaskUuid,
        message: emailContent.message.replaceAll("\n", "<br>"),
        subject: emailContent.subject.replaceAll("\n", "<br>"),
        wsWorkspaceUuid: activeWorkspaceUuid,
        wsProjectUuid: params.projectId,
      });

      setPreviewData(response.content);
    } catch (err) {
      showErrorToast();
    }
  };

  const decodeMessage = (content: string) => {
    const parts = content.split(/\[!(.*?)\]/g);

    for (let i = 1; i < parts.length; i += 2) {
      // @ts-ignore
      parts[i] = `<span class='highlighted-message'>[! ${parts[i]}]</span>`;
    }

    return parts.join("");
  };

  const decodeTextForEmailPreview = (value: string) => {
    const decodedValue = decodeMessage(
      decodeMention(decodeHyperlinks(value), [], true),
    );

    return XssFilter(
      decodedValue,
      ["span", "br", "a", "b", "i", "u", "strong"],
      ["target", "class", "rel", "contenteditable", "href"],
    );
  };

  return (
    <div className="email-preview">
      <div className="email-preview__recipient">
        <h4 className="email-preview__header">
          <IDHFormattedMessage id="ws_recipient" defaultMessage="Recipient" />:
        </h4>
        <div className="email-preview__recipient-wrapper">
          {emailContent.recipients.length > 1 && (
            <span
              className="email-preview__recipient-chevron"
              onClick={toPreviousRecipient}
            >
              <ChevronLeft />
            </span>
          )}
          <div className="email-preview__recipient-data">
            <img
              src={
                selectedRecipient?.avatar?.thumbnails?.tiny ||
                CreatorPlaceholder
              }
              alt={selectedRecipient?.title}
            />
            <span className="email-preview__recipient-data-name">
              {selectedRecipient?.title}
            </span>
          </div>
          {emailContent.recipients.length > 1 && (
            <span
              className="email-preview__recipient-chevron"
              onClick={toNextRecipient}
            >
              <ChevronRight />
            </span>
          )}
        </div>
      </div>

      {previewData && (
        <AnimatedDiv>
          <div className="email-preview__subject">
            <h4 className="email-preview__header">
              <IDHFormattedMessage id="ws_subject" defaultMessage="Subject" />:
            </h4>
            <p
              dangerouslySetInnerHTML={{
                __html: decodeTextForEmailPreview(previewData.subject),
              }}
            />
          </div>

          <div className="email-preview__message">
            <h4 className="email-preview__header">
              <IDHFormattedMessage id="ws_message" defaultMessage="Message" />:
            </h4>
            <p
              dangerouslySetInnerHTML={{
                __html: decodeTextForEmailPreview(previewData.message),
              }}
            />
          </div>
        </AnimatedDiv>
      )}
    </div>
  );
}
